/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { Text } from '@atlaskit/primitives/compiled';
import ProfileClient from '@atlaskit/profilecard/client';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import { messages } from '../../common/messages';

const metadataStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	minHeight: token('space.300'),
	color: token('color.text'),
	font: token('font.body.small'),
});

const profileTriggerStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	minHeight: token('space.300'),
	gap: token('space.050'),
	color: token('color.text'),
	font: token('font.body.small'),
	backgroundColor: 'transparent',
	borderWidth: 0,
	paddingBlock: 0,
	paddingInline: 0,
	cursor: 'pointer',
});

const DIRECTORY_URL = '/gateway/api/directory/graphql';

const profileClient = new ProfileClient({
	url: DIRECTORY_URL,
});

type OwnerProfile = {
	avatarUrl?: string;
	fullName?: string;
	nickname?: string;
};

const getLastSuccessfulExecutionDate = (aimoData: AiManagedObject | null): Date | null => {
	if (aimoData?.lastExecutionStatus !== 'SUCCESS' || !aimoData.lastExecutedAt) {
		return null;
	}

	const date = new Date(aimoData.lastExecutedAt);
	return Number.isNaN(date.getTime()) ? null : date;
};

type AimoStatusMetadataProps = {
	/** Hydrated AI-managed object metadata for the configured block. */
	aimoData?: AiManagedObject | null;
	/** Site cloud ID, used for profilecard hydration. */
	cloudId?: string;
};

export const AimoStatusMetadata = ({
	aimoData = null,
	cloudId,
}: AimoStatusMetadataProps): JSX.Element | null => {
	const intl = useIntl();
	const ownerId = aimoData?.lastModifiedBy;
	const lastSuccessfulExecutionDate = getLastSuccessfulExecutionDate(aimoData);
	const [ownerProfile, setOwnerProfile] = useState<OwnerProfile | null>(null);

	useEffect(() => {
		if (!ownerId || !cloudId) {
			setOwnerProfile(null);
			return;
		}

		let cancelled = false;
		setOwnerProfile(null);

		profileClient
			.getProfile(cloudId, ownerId)
			.then((profile: OwnerProfile | null | undefined) => {
				if (!cancelled) {
					setOwnerProfile(profile ?? null);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setOwnerProfile(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [cloudId, ownerId]);

	const ownerName = ownerProfile?.fullName ?? ownerProfile?.nickname;

	const hasOwner = Boolean(ownerId && cloudId && ownerName);
	const hasUpdatedDate = Boolean(lastSuccessfulExecutionDate);

	if (!(hasOwner || hasUpdatedDate)) {
		return null;
	}

	return (
		<div css={metadataStyles}>
			{hasOwner && ownerId && cloudId && ownerName && (
				<ProfileCardTrigger
					cloudId={cloudId}
					prepopulatedData={ownerProfile ?? undefined}
					resourceClient={profileClient}
					userId={ownerId}
				>
					<Tooltip content={intl.formatMessage(messages.owner)}>
						<button type="button" css={profileTriggerStyles}>
							<Avatar
								appearance="circle"
								label={ownerName}
								size="xxsmall"
								src={ownerProfile?.avatarUrl}
							/>
							{ownerName}
						</button>
					</Tooltip>
				</ProfileCardTrigger>
			)}
			{hasOwner && hasUpdatedDate && (
				<Text as="span" color="color.text.subtlest">
					|
				</Text>
			)}
			{lastSuccessfulExecutionDate && (
				<span>
					<Text as="span" color="color.text.subtle">
						{intl.formatMessage(messages.lastSuccessfulExecution)}
					</Text>{' '}
					{intl.formatDate(lastSuccessfulExecutionDate, {
						day: 'numeric',
						month: 'short',
						year: 'numeric',
					})}{' '}
					{intl.formatTime(lastSuccessfulExecutionDate, {
						hour: 'numeric',
						minute: '2-digit',
					})}
				</span>
			)}
		</div>
	);
};
